/** @jsxImportSource @emotion/react */
import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Radio, Space, Typography, Alert, Descriptions, message } from 'antd';
import type { WeatherGapInfo, WeatherExtendSource } from '../../types/simulation';
import { useExtendWeatherSeriesMutation } from '../../api/simulationApi';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

interface WeatherGapDialogProps {
  open: boolean;
  gap: WeatherGapInfo | null;
  onClose: () => void;
  onExtended?: () => void;
}

const sourceLabels = (): Record<WeatherExtendSource, { title: string; hint: string }> => ({
  nasa_power: {
    title: 'NASA POWER',
    hint: i18n.t('weather:gap.sources.nasaPower.hint'),
  },
  open_meteo: {
    title: 'Open-Meteo Archive',
    hint: i18n.t('weather:gap.sources.openMeteo.hint'),
  },
  synthetic_climatology: {
    title: i18n.t('weather:gap.sources.syntheticClimatology.title'),
    hint: i18n.t('weather:gap.sources.syntheticClimatology.hint'),
  },
});

const DEFAULT_SUGGESTIONS: WeatherExtendSource[] = ['nasa_power', 'open_meteo', 'synthetic_climatology'];

const WeatherGapDialog: React.FC<WeatherGapDialogProps> = ({ open, gap, onClose, onExtended }) => {
  const { t } = useTranslation('weather');
  const [source, setSource] = useState<WeatherExtendSource>('nasa_power');
  const [extendWeatherSeries, { isLoading }] = useExtendWeatherSeriesMutation();

  useEffect(() => {
    if (open && gap?.suggestions?.length) {
      setSource(gap.suggestions[0]);
    }
  }, [open, gap?.suggestions]);

  const suggestions = useMemo<WeatherExtendSource[]>(
    () => (gap?.suggestions?.length ? gap.suggestions : DEFAULT_SUGGESTIONS),
    [gap?.suggestions],
  );
  const coordinates = gap?.coordinates && Number.isFinite(gap.coordinates.latitude) && Number.isFinite(gap.coordinates.longitude)
    ? gap.coordinates
    : null;

  if (!gap) return null;

  const handleConfirm = async () => {
    if (!gap.seriesId) {
      message.error(t('gap.errors.noSeriesId'));
      return;
    }
    try {
      const result = await extendWeatherSeries({
        seriesId: gap.seriesId,
        startDate: gap.requestedStartDate,
        endDate: gap.requestedEndDate,
        source,
        coords: coordinates,
      }).unwrap();
      message.success(t('gap.loaded', { count: result.pointCount, start: result.startDate, end: result.endDate }));
      onExtended?.();
      onClose();
    } catch (err: any) {
      const text = err?.data?.message || err?.message || t('gap.errors.extendFailed');
      message.error(text);
    }
  };

  const labels = sourceLabels();

  return (
    <Modal
      open={open}
      title={t('gap.title')}
      onCancel={onClose}
      onOk={handleConfirm}
      okText={t('gap.load')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isLoading}
      width={620}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        message={t('gap.notCovered')}
        description={gap.reason || t('gap.needMorePoints')}
        style={{ marginBottom: 16 }}
      />

      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('gap.series')}>
          {gap.seriesName || gap.seriesId || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('gap.missingRange')}>
          {gap.requestedStartDate} → {gap.requestedEndDate}
          {gap.missingDate ? ` ${t('gap.missingSince', { date: gap.missingDate })}` : ''}
        </Descriptions.Item>
        {gap.seriesStartDate && gap.seriesEndDate && (
          <Descriptions.Item label={t('gap.seriesBounds')}>
            {String(gap.seriesStartDate).slice(0, 10)} → {String(gap.seriesEndDate).slice(0, 10)}
          </Descriptions.Item>
        )}
        {coordinates && (
          <Descriptions.Item label={t('gap.coordinates')}>
            {coordinates.latitude.toFixed(4)}, {coordinates.longitude.toFixed(4)}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Text strong>{t('gap.dataSource')}</Typography.Text>
      <Radio.Group
        value={source}
        onChange={(e) => setSource(e.target.value as WeatherExtendSource)}
        style={{ marginTop: 8, width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {suggestions.map((src) => {
            const meta = labels[src];
            if (!meta) return null;
            return (
              <Radio key={src} value={src}>
                <div>
                  <div><strong>{meta.title}</strong></div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>{meta.hint}</Typography.Text>
                </div>
              </Radio>
            );
          })}
        </Space>
      </Radio.Group>
    </Modal>
  );
};

export default WeatherGapDialog;
